import type { IMatchDetails } from "./matchesDetails/types/matchDetails.d";
import { officialScheduleId } from "./scheduleMatchPrefillUtils";

/**
 * Match officials as the backend stores them (POST scheduleMatch `officials[]`, PATCH match/{id}/officials).
 * Same roles and wire values the mobile app sends.
 */
export type OfficialRole =
  | "ON_FIELD_UMPIRE_1"
  | "ON_FIELD_UMPIRE_2"
  | "THIRD_UMPIRE"
  | "FOURTH_UMPIRE"
  | "SCORER_1"
  | "SCORER_2"
  | "COMMENTATOR_1"
  | "COMMENTATOR_2"
  | "MATCH_REFEREE"
  | "STREAMER_1";

export type OfficialProfileType = "PLAYER" | "TEAM" | "NONE";

export interface MatchOfficialPayload {
  role: OfficialRole;
  profileType: OfficialProfileType;
  profileId?: string;
  name?: string;
}

/** An entry from league/availableUmpires or availableScorers. `isPerson` = the API's isUmpire / isScorer flag. */
export interface OfficialPick {
  id: string;
  name: string;
  isPerson: boolean;
}

/** One list-or-typed slot (umpires, scorers). A picked profile wins over a typed name. */
export interface OfficialSlot {
  pick: OfficialPick | null;
  manualName: string;
}

export interface OfficialsForm {
  umpire1: OfficialSlot;
  umpire2: OfficialSlot;
  umpire3: OfficialSlot;
  umpire4: OfficialSlot;
  scorer1: OfficialSlot;
  scorer2: OfficialSlot;
  commentator1: string;
  commentator2: string;
  referee: string;
  broadcaster: string;
}

export type SlotKey = "umpire1" | "umpire2" | "umpire3" | "umpire4" | "scorer1" | "scorer2";
export type TextKey = "commentator1" | "commentator2" | "referee" | "broadcaster";

export const SLOT_ROLE: Record<SlotKey, OfficialRole> = {
  umpire1: "ON_FIELD_UMPIRE_1",
  umpire2: "ON_FIELD_UMPIRE_2",
  umpire3: "THIRD_UMPIRE",
  umpire4: "FOURTH_UMPIRE",
  scorer1: "SCORER_1",
  scorer2: "SCORER_2",
};

export const TEXT_ROLE: Record<TextKey, OfficialRole> = {
  commentator1: "COMMENTATOR_1",
  commentator2: "COMMENTATOR_2",
  referee: "MATCH_REFEREE",
  broadcaster: "STREAMER_1",
};

export const emptySlot = (): OfficialSlot => ({ pick: null, manualName: "" });

export const emptyOfficials = (): OfficialsForm => ({
  umpire1: emptySlot(),
  umpire2: emptySlot(),
  umpire3: emptySlot(),
  umpire4: emptySlot(),
  scorer1: emptySlot(),
  scorer2: emptySlot(),
  commentator1: "",
  commentator2: "",
  referee: "",
  broadcaster: "",
});

/** The full officials list for the request. The backend replaces ALL stored officials with whatever is sent, so send every filled role. */
export function toOfficialsRequest(f: OfficialsForm): MatchOfficialPayload[] {
  const out: MatchOfficialPayload[] = [];
  (Object.keys(SLOT_ROLE) as SlotKey[]).forEach((k) => {
    const slot = f[k];
    const role = SLOT_ROLE[k];
    if (slot.pick) {
      out.push({ role, profileType: slot.pick.isPerson ? "PLAYER" : "TEAM", profileId: slot.pick.id });
    } else if (slot.manualName.trim()) {
      out.push({ role, profileType: "NONE", name: slot.manualName.trim() });
    }
  });
  (Object.keys(TEXT_ROLE) as TextKey[]).forEach((k) => {
    const name = f[k].trim();
    if (name) out.push({ role: TEXT_ROLE[k], profileType: "NONE", name });
  });
  return out;
}

/** Older request fields (umpireId / scorerId + is-a-person flags) the backend still reads; the app sends them too. */
export function legacyOfficialFields(f: OfficialsForm) {
  const p = (s: OfficialSlot) => s.pick;
  return {
    umpireId: p(f.umpire1)?.id,
    isUmpire: p(f.umpire1) ? p(f.umpire1)!.isPerson : undefined,
    umpireTwoId: p(f.umpire2)?.id,
    isUmpireTwoPerson: p(f.umpire2) ? p(f.umpire2)!.isPerson : undefined,
    thirdUmpireId: p(f.umpire3)?.id,
    isThirdUmpirePerson: p(f.umpire3) ? p(f.umpire3)!.isPerson : undefined,
    scorerId: p(f.scorer1)?.id,
    isScorer: p(f.scorer1) ? p(f.scorer1)!.isPerson : undefined,
  };
}

/** Prefill from a loaded match: `officials[]` when present, else the older umpire / scorer fields. */
export function officialsFromMatch(m: IMatchDetails): OfficialsForm {
  const f = emptyOfficials();
  const list = m.officials ?? [];
  if (list.length > 0) {
    const bySlot = Object.fromEntries((Object.keys(SLOT_ROLE) as SlotKey[]).map((k) => [SLOT_ROLE[k], k])) as Record<string, SlotKey>;
    const byText = Object.fromEntries((Object.keys(TEXT_ROLE) as TextKey[]).map((k) => [TEXT_ROLE[k], k])) as Record<string, TextKey>;
    for (const o of list) {
      const slotKey = bySlot[o.role];
      const textKey = byText[o.role];
      if (slotKey) {
        if ((o.profileType === "PLAYER" || o.profileType === "TEAM") && o.profileId) {
          f[slotKey] = { pick: { id: o.profileId, name: o.name ?? "", isPerson: o.profileType === "PLAYER" }, manualName: "" };
        } else if (o.name) {
          f[slotKey] = { pick: null, manualName: o.name };
        }
      } else if (textKey && o.name) {
        f[textKey] = o.name;
      }
    }
    return f;
  }
  const legacy = (o: IMatchDetails["umpire"]): OfficialSlot => {
    const id = officialScheduleId(o ?? null);
    if (!id) return emptySlot();
    const name = (o?.name ?? [o?.playerFirstName, o?.playerLastName].filter(Boolean).join(" ")) || "";
    return { pick: { id, name: String(name), isPerson: true }, manualName: "" };
  };
  f.umpire1 = legacy(m.umpire);
  f.umpire2 = legacy(m.umpireTwo);
  f.umpire3 = legacy(m.thirdUmpire);
  f.scorer1 = legacy(m.scorer);
  return f;
}
